import { Typography } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import { isFunction } from 'lodash';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import { usePadStart } from 'shared/hooks';

interface RemainingTimeProps {
  date: string;
  onComplete?: () => void;
  variant?: Variant;
}

const RemainingTimeComponent: FC<RemainingTimeProps> = ({
  date,
  onComplete,
  variant = 'subtitle2',
}) => {
  const isComplete = useMemo(() => moment().diff(date) > 0, [date]);
  const { padStart } = usePadStart();

  // The `Countdown` does not call the `onComplete` function if the timer is already expired,
  //   that's why the check and call of `onComplete` made here.
  useEffect(() => {
    if (isComplete && isFunction(onComplete)) {
      onComplete();
    }
  }, [isComplete, onComplete]);

  const updateTimeHandler = useCallback(
    (props: CountdownRenderProps) => {
      if (!props.total) {
        return null;
      }

      const { days, hours, minutes, seconds } = props;
      const color = !days && !hours && !minutes && seconds ? 'error.light' : 'inherit';

      let content = '';
      if (days >= 1) {
        content = `${days}d ${padStart(hours)}h`;
      } else if (!days && hours) {
        content = `${padStart(hours)}:${padStart(minutes)}:${padStart(seconds)}`;
      } else if ((!hours && minutes) || (!minutes && seconds)) {
        content = `${padStart(minutes)}:${padStart(seconds)}`;
      }

      if (props.total > 0) {
        return (
          <Typography component="span" variant={variant} sx={{ color }}>
            {content}
          </Typography>
        );
      }

      return null;
    },
    [padStart, variant]
  );

  return <Countdown date={date} renderer={updateTimeHandler} onComplete={onComplete} />;
};

export const RemainingTime = memo(RemainingTimeComponent);
